import {
  AppointmentResponseDto,
  CreateAppointmentRequestDto,
  ResponseError,
  UpdateAppointmentRequestDto,
} from "libs/cellera-sdk";
import { useState, useCallback } from "react";
import { AppointmentFormData } from "validations/appointmentSchemas";

import { appointmentApi } from "../apis";
import { COLOR } from "../constants/common";

const colors = [
  COLOR.RED,
  COLOR.TEAL,
  COLOR.BLUE,
  COLOR.GREEN,
  COLOR.YELLOW,
  COLOR.PINK,
  COLOR.MINT,
  COLOR.GOLD,
  COLOR.LAVENDER,
  COLOR.LIGHT_BLUE,
];

export interface AppointmentData {
  id?: string;
  patientId: string;
  providerId?: string;
  startDate: Date;
  startTime: Date;
  endTime: Date;
  colorCode?: number;
  notes?: string;
  patientName?: string;
  patientEmail?: string;
}

export interface UseAppointmentsReturn {
  appointments: AppointmentFormData[];
  loading: boolean;
  error: string | null;
  isForbidden: boolean;
  createAppointment: (data: AppointmentFormData) => Promise<void>;
  updateAppointment: (id: string, data: AppointmentFormData) => Promise<void>;
  deleteAppointment: (id: string) => Promise<void>;
  fetchAppointments: (startDate?: Date, endDate?: Date) => Promise<void>;
  refreshAppointments: () => Promise<void>;
  getColorByCode: (code?: number) => string;
}

// Generate random colors for appointments
export const getRandomColorCode = () => {
  return Math.floor(Math.random() * colors.length);
};

const getColorByCode = (code?: number) => {
  return colors[code ?? getRandomColorCode()] || colors[getRandomColorCode()];
};

const convertToAppointmentDto = (
  data: AppointmentFormData
): CreateAppointmentRequestDto => {
  if (!data.startDate || !data.startTime || !data.endTime) {
    throw new Error("Start date, start time, and end time are required");
  }

  // Combine date and time
  const startDateTime = new Date(data.startDate);
  startDateTime.setHours(
    data.startTime.getHours(),
    data.startTime.getMinutes(),
    0,
    0
  );

  const endDateTime = new Date(data.startDate);
  endDateTime.setHours(
    data.endTime.getHours(),
    data.endTime.getMinutes(),
    0,
    0
  );

  return {
    patientId: data.patientId,
    providerId: data.providerId,
    colorCode: data.colorCode,
    startTime: startDateTime,
    endTime: endDateTime,
    notes: data.notes,
  };
};

// Helper function to convert API response to frontend format
const convertFromApiFormat = (
  apiAppointment: AppointmentResponseDto
): AppointmentData => {
  const appointmentDate = new Date(apiAppointment.appointmentDate);
  // Parse API times as Date objects to get local timezone values
  const apiStartTime = new Date(apiAppointment.startTime);
  const apiEndTime = new Date(apiAppointment.endTime);

  // Extract local timezone hours and minutes
  const startHours = apiStartTime.getHours();
  const startMinutes = apiStartTime.getMinutes();
  const endHours = apiEndTime.getHours();
  const endMinutes = apiEndTime.getMinutes();

  const startTime = new Date(appointmentDate);
  startTime.setHours(startHours, startMinutes, 0, 0);

  const endTime = new Date(appointmentDate);
  endTime.setHours(endHours, endMinutes, 0, 0);

  return {
    id: apiAppointment.id,
    patientId: apiAppointment.patientId,
    providerId: apiAppointment.providerId,
    startDate: appointmentDate,
    startTime: startTime,
    endTime: endTime,
    colorCode: apiAppointment.colorCode,
    patientName:
      apiAppointment.patient.firstName + " " + apiAppointment.patient.lastName,
    patientEmail: apiAppointment.patient.email,
    notes: apiAppointment.notes,
  };
};

export const useAppointments = (): UseAppointmentsReturn => {
  const [appointments, setAppointments] = useState<AppointmentData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isForbidden, setIsForbidden] = useState(false);

  const fetchAppointments = useCallback(
    async (startDate?: Date, endDate?: Date) => {
      try {
        setLoading(true);
        setError(null);

        const response = await appointmentApi.getAppointmentsByProviderRaw({
          startDate,
          endDate,
          orderBy: "ASC",
        });

        const allAppointments = await response.value();
        const convertedAppointments = allAppointments.map(convertFromApiFormat);
        setAppointments(convertedAppointments);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch appointments";
        setError(errorMessage);
        if (err instanceof ResponseError && err.response.status === 403) {
          setIsForbidden(true);
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createAppointment = useCallback(
    async (data: AppointmentFormData) => {
      try {
        setLoading(true);
        setError(null);

        const appointmentData = {
          ...data,
          colorCode: data.colorCode || getRandomColorCode(),
        };

        const apiData = convertToAppointmentDto(appointmentData);
        await appointmentApi.createAppointment({
          createAppointmentRequestDto: apiData,
        });

        await fetchAppointments();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create appointment";
        setError(errorMessage);
        if (err instanceof ResponseError && err.response.status === 403) {
          setIsForbidden(true);
        }
      } finally {
        setLoading(false);
      }
    },
    [fetchAppointments]
  );

  const updateAppointment = useCallback(
    async (id: string, data: AppointmentFormData) => {
      try {
        setLoading(true);
        setError(null);

        const startTime = new Date(data.startDate);
        startTime.setHours(
          data.startTime.getHours(),
          data.startTime.getMinutes(),
          0,
          0
        );
        const endTime = new Date(data.startDate);
        endTime.setHours(
          data.endTime.getHours(),
          data.endTime.getMinutes(),
          0,
          0
        );

        const apiData: UpdateAppointmentRequestDto = {
          startTime,
          endTime,
          notes: data.notes,
        };
        await appointmentApi.updateAppointment({
          id,
          updateAppointmentRequestDto: apiData,
        });

        await fetchAppointments();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update appointment";
        setError(errorMessage);
        if (err instanceof ResponseError && err.response.status === 403) {
          setIsForbidden(true);
        }
      } finally {
        setLoading(false);
      }
    },
    [fetchAppointments]
  );

  const deleteAppointment = useCallback(
    async (id: string) => {
      try {
        setLoading(true);
        setError(null);

        await appointmentApi.deleteAppointment({ id });

        // Refresh the appointments list
        await fetchAppointments();
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete appointment";
        setError(errorMessage);
        if (err instanceof ResponseError && err.response.status === 403) {
          setIsForbidden(true);
        }
      } finally {
        setLoading(false);
      }
    },
    [fetchAppointments]
  );

  const refreshAppointments = useCallback(async () => {
    await fetchAppointments();
  }, [fetchAppointments]);

  // Note: Initial fetch is handled by the component with specific date range

  return {
    appointments,
    loading,
    error,
    isForbidden,
    createAppointment,
    updateAppointment,
    deleteAppointment,
    fetchAppointments,
    refreshAppointments,
    getColorByCode,
  };
};
